"use client";

/**
 * Hub do levantamento — dirigido por status (#299 D1/D2).
 *
 * status=criado         → painel inbox: lista, multi-seleção, anexar, processar
 * status=em_processamento → vista de progresso
 * status=processado/validated → "Editar Cut Lines" habilitado
 * status=erro           → mensagem de erro + opção de re-processar
 *
 * Fluxo D2 (SPEC-video-fluxo-operador D2):
 *   - "Procurar novos vídeos" = POST /videos/inbox/scan (fecha G4)
 *   - "Anexar" = POST /levantamentos/{id}/videos (multi-seleção com ordem)
 *   - "Processar" = POST /levantamentos/{id}/process — BOTÃO EXPLÍCITO, nunca automático
 *   - Inbox path vem de GET /videos/config (fecha G6)
 */

import { useState } from "react";
import { useParams, useRouter } from "next/navigation";
import { RefreshCwIcon } from "lucide-react";
import { toast } from "sonner";

import {
  useAssociateVideos,
  useCountRuns,
  useCutLines,
  useDispatchLevantamento,
  useInboxVideos,
  useLevantamento,
  useLevantamentos,
  useProcessLevantamento,
  useScanInbox,
  useVideoConfig,
} from "@/lib/api-video";
import { describeFilaPlacar } from "@/lib/fila-helpers";
import { formatDateTime } from "@/lib/formatters";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FilaPlacar } from "@/components/video/fila-placar";
import { RetentionBadge } from "@/components/video/retention-badge";
import { VideoInboxHint } from "@/components/video/video-inbox-hint";
import { WizardStepper } from "@/components/video/wizard-stepper";
import { EditLevantamentoDialog } from "./edit-levantamento-dialog";
import { ReprocessarLevantamentoDialog } from "./reprocessar-dialog";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds: string): string {
  const s = Math.round(Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h${m.toString().padStart(2, "0")}m`;
  return `${m}m${sec.toString().padStart(2, "0")}s`;
}

// ---------------------------------------------------------------------------
// Sub-panel: inbox listing + multi-select + attach + process
// ---------------------------------------------------------------------------

interface InboxPanelProps {
  levId: string;
  /** Videos already associated to this levantamento (to prevent re-association). */
  associatedVideoIds: string[];
}

function InboxPanel({ levId, associatedVideoIds }: InboxPanelProps) {
  const { data: inbox, isLoading, error } = useInboxVideos();
  const { mutate: scan, isPending: isScanning } = useScanInbox();
  const { mutate: associate, isPending: isAssociating } =
    useAssociateVideos(levId);
  const { mutate: process, isPending: isProcessing } =
    useProcessLevantamento(levId);

  // Selected video ids (for multi-select)
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // IDs already owned by this levantamento are excluded from inbox list
  const available = (inbox ?? []).filter(
    (v) => !associatedVideoIds.includes(v.id),
  );

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === available.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(available.map((v) => v.id)));
    }
  }

  function handleScan() {
    scan(undefined, {
      onSuccess: (result) => {
        if (result.detected_now > 0) {
          toast.success(
            `${result.detected_now} vídeo(s) detectado(s). Total na inbox: ${result.total}.`,
          );
        } else {
          toast.info(
            result.total === 0
              ? "Nenhum vídeo na pasta inbox."
              : `Nenhum vídeo novo. ${result.total} já conhecido(s).`,
          );
        }
      },
      onError: () => toast.error("Falha ao escanear inbox."),
    });
  }

  function handleAttach() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;

    // Ordem: 1-based, na ordem cronológica (por file_name, como o worker faz)
    const sorted = available
      .filter((v) => selected.has(v.id))
      .sort((a, b) => a.file_name.localeCompare(b.file_name));

    const ordemMap: Record<string, number> = {};
    sorted.forEach((v, i) => {
      ordemMap[v.id] = i + 1;
    });

    associate(
      { video_ids: ids, ordem: ordemMap },
      {
        onSuccess: (videos) => {
          toast.success(`${videos.length} vídeo(s) anexado(s).`);
          setSelected(new Set());
        },
        onError: () => toast.error("Falha ao anexar vídeos."),
      },
    );
  }

  function handleProcess() {
    process(undefined, {
      onSuccess: (result) => {
        toast.success(
          `Processamento iniciado. ${result.videos_queued} vídeo(s) na fila.`,
        );
      },
      onError: () =>
        toast.error(
          "Falha ao iniciar processamento. Verifique se há vídeos anexados.",
        ),
    });
  }

  const hasAssociated = associatedVideoIds.length > 0;

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">
            Vídeos esperando na pasta
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Selecione os vídeos deste posto e clique em Anexar. Depois, clique
            em Processar para iniciar a análise.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleScan}
          disabled={isScanning}
          data-testid="btn-scan-inbox"
        >
          <RefreshCwIcon
            className={`size-3.5 ${isScanning ? "animate-spin" : ""}`}
          />
          {isScanning ? "Escaneando…" : "Procurar novos vídeos"}
        </Button>
      </div>

      {/* Loading / error / empty states — anti-fabrication: never blank */}
      {isLoading ? (
        <div className="text-sm text-muted-foreground">
          Carregando inbox…
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertTitle>Erro ao carregar inbox</AlertTitle>
          <AlertDescription>
            {(error as Error)?.message ?? "Erro desconhecido."}
          </AlertDescription>
        </Alert>
      ) : available.length === 0 && !hasAssociated ? (
        <Alert>
          <AlertTitle>Nenhum vídeo na inbox</AlertTitle>
          <AlertDescription>
            Solte os arquivos na pasta inbox do servidor e clique em{" "}
            <strong>Procurar novos vídeos</strong>. O worker detecta
            automaticamente a cada ~10s.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Inbox table */}
      {available.length > 0 && (
        <div className="rounded-md border">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/40 text-left">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Selecionar todos"
                    checked={selected.size === available.length}
                    onChange={toggleAll}
                    className="rounded"
                  />
                </th>
                <th className="px-3 py-2">Arquivo</th>
                <th className="px-3 py-2 w-24">Duração</th>
                <th className="px-3 py-2 w-24">Tamanho</th>
                <th className="px-3 py-2 w-32">Resolução</th>
                <th className="px-3 py-2 w-40">Detectado em</th>
              </tr>
            </thead>
            <tbody>
              {available
                .slice()
                .sort((a, b) => a.file_name.localeCompare(b.file_name))
                .map((v) => (
                  <tr
                    key={v.id}
                    onClick={() => toggleSelect(v.id)}
                    className="border-b last:border-0 cursor-pointer hover:bg-muted/30 transition-colors"
                    data-testid={`inbox-row-${v.id}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(v.id)}
                        onChange={() => toggleSelect(v.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded"
                        aria-label={`Selecionar ${v.file_name}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {v.file_name}
                    </td>
                    <td className="px-3 py-2">
                      {formatDuration(v.duration_seconds)}
                    </td>
                    <td className="px-3 py-2">
                      {formatSize(v.file_size_bytes)}
                    </td>
                    <td className="px-3 py-2">{v.resolution ?? "—"}</td>
                    <td className="px-3 py-2">
                      {formatDateTime(v.detected_at)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Already-attached info */}
      {hasAssociated && (
        <p className="text-xs text-muted-foreground">
          {associatedVideoIds.length} vídeo(s) já anexado(s) a este
          levantamento.
        </p>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          onClick={handleAttach}
          disabled={selected.size === 0 || isAssociating}
          data-testid="btn-attach-videos"
        >
          {isAssociating
            ? "Anexando…"
            : `Anexar ${selected.size > 0 ? `${selected.size} vídeo(s)` : ""}`}
        </Button>

        {/* Processar é botão EXPLÍCITO SEPARADO — nunca automático ao anexar (D2) */}
        <Button
          variant="outline"
          onClick={handleProcess}
          disabled={!hasAssociated || isProcessing}
          data-testid="btn-process"
          title={
            !hasAssociated
              ? "Anexe ao menos um vídeo antes de processar"
              : "Inicia o processamento ML (4-6h por vídeo)"
          }
        >
          {isProcessing ? "Iniciando…" : "Processar"}
        </Button>

        {!hasAssociated && (
          <span className="text-xs text-muted-foreground">
            Anexe vídeos para habilitar o processamento
          </span>
        )}
      </div>

      {/* Inbox path hint */}
      <VideoInboxHint variant="compact" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-panel: em_processamento
// ---------------------------------------------------------------------------

interface ProcessingPanelProps {
  avgProgress: number;
  videos: Array<{ status: string; progress: string; file_name: string }>;
}

function ProcessingPanel({ avgProgress, videos }: ProcessingPanelProps) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Processando vídeos</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Pipeline ML em execução. Esta etapa pode levar 4-6h por vídeo.
        </p>
      </div>

      <Card className="p-4 space-y-3">
        <div className="text-sm">
          <span className="font-medium">Progresso médio: </span>
          {Math.round(avgProgress * 100)}%
        </div>
        {/* Progress bar */}
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-tide-accent transition-all"
            style={{ width: `${Math.round(avgProgress * 100)}%` }}
          />
        </div>

        {/* Per-video status */}
        {videos.length > 0 && (
          <div className="space-y-1 pt-1">
            {videos.map((v, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-xs"
              >
                <span className="font-mono truncate max-w-[280px]">
                  {v.file_name}
                </span>
                <span className="text-muted-foreground ml-2 shrink-0">
                  {v.status} · {Math.round(Number(v.progress) * 100)}%
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="text-xs text-muted-foreground">
        A página atualiza automaticamente quando o processamento concluir.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function LevantamentoOverviewPage() {
  const params = useParams();
  const router = useRouter();
  const levId = params.lev_id as string;

  // #331 — edit metadata dialog state (must be before early returns)
  const [editOpen, setEditOpen] = useState(false);
  // #434 — confirmação de re-processamento (clone com vínculo + vigência)
  const [reprocessarOpen, setReprocessarOpen] = useState(false);

  const { data, isLoading, error } = useLevantamento(levId);

  // #426 — placar da fila: lista completa (posição FIFO) + teto configurado
  // + mutation do botão Reprocessar. Hooks sempre antes dos early returns.
  const { data: allLevantamentos = [] } = useLevantamentos({});
  const { data: videoConfig } = useVideoConfig();
  const dispatchMut = useDispatchLevantamento(levId);
  // #483 — insumo do WizardStepper (ordem travada): Cut-lines libera com
  // cutLines.length>0, Revisar/Baixar liberam com runs.length>0.
  const { data: cutLines = [] } = useCutLines(levId);
  const { data: countRuns = [] } = useCountRuns(levId);

  if (isLoading) {
    return (
      <div className="p-6 text-muted-foreground">Carregando levantamento…</div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-6 text-destructive">
        Erro:{" "}
        {(error as Error)?.message ?? "levantamento não encontrado"}
      </div>
    );
  }

  const isProcessed = data.status === "processado";
  // #483 — Revisar/Baixar só fazem sentido a partir de um count_run real;
  // "Ver resultados" gated nisso embaixo previne o beco-sem-saída do achado 1
  // (operador ia direto pra Resultados sem nunca ter clicado Calcular).
  const hasRuns = countRuns.length > 0;
  const avgProgress =
    data.videos.length === 0
      ? 0
      : data.videos.reduce((acc, v) => acc + Number(v.progress), 0) /
        data.videos.length;

  const associatedVideoIds = data.videos.map((v) => v.id);

  // #426 — placar da fila (gesto 3) + reprocessar (gesto 2). 'aberto' (ainda
  // não despachado) e 'pronto_para_revisao' (já coberto pelas seções
  // Editar Cut Lines/Ver resultados abaixo, que reagem a `status`) não
  // ganham card próprio — evita placar redundante/vazio.
  const filaInfo = describeFilaPlacar(
    {
      id: data.id,
      fila_status: data.fila_status,
      fila_error_message: data.fila_error_message,
      fila_entered_at: data.fila_entered_at,
    },
    allLevantamentos,
    videoConfig?.fila_max_concurrent ?? null,
  );
  const showFilaPlacar =
    filaInfo.status !== "aberto" && filaInfo.status !== "pronto_para_revisao";

  // #434 — clone com vínculo + vigência: localiza o clone sucessor (se
  // este levantamento já foi superado) e o levantamento de origem (se este
  // É um clone), a partir da mesma lista já carregada pro placar da fila —
  // sem endpoint novo.
  const successorClone = allLevantamentos.find(
    (l) => l.origem_levantamento_id === data.id,
  );
  const origemLevantamento = data.origem_levantamento_id
    ? allLevantamentos.find((l) => l.id === data.origem_levantamento_id)
    : undefined;

  function handleReprocess() {
    dispatchMut.mutate(undefined, {
      onSuccess: () =>
        toast.success(
          "Reprocessamento disparado — a fila retoma o levantamento.",
        ),
      onError: (err) =>
        toast.error((err as Error)?.message ?? "Falha ao reprocessar."),
    });
  }

  return (
    <div className="flex flex-col">
      {/* #483 — wizard stepper visível (trilha inteira, ordem travada) */}
      <WizardStepper
        levId={levId}
        current="upload"
        videoReady={isProcessed || data.status === "validated"}
        hasCutLines={cutLines.length > 0}
        hasRuns={hasRuns}
        onNavigate={(href) => router.push(href)}
      />
      <div className="space-y-4 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{data.posto_contagem}</h1>
          <p className="text-muted-foreground">
            {data.rodovia ?? "—"} · sentido {data.sentido} · Início da gravação:{" "}
            {formatDateTime(data.data_hora_inicial)}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="btn-edit-metadata"
          onClick={() => setEditOpen(true)}
        >
          Editar dados do posto
        </Button>
      </div>

      {/* #331 — edit metadata dialog (mount only while open → fresh prefill) */}
      {editOpen && (
        <EditLevantamentoDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          levantamento={data}
        />
      )}

      {/* #434 — diálogo de confirmação do reprocessamento (clone com
          vínculo + vigência). Mount só enquanto aberto (mesmo padrão do
          diálogo de edição acima). */}
      {reprocessarOpen && (
        <ReprocessarLevantamentoDialog
          open={reprocessarOpen}
          onOpenChange={setReprocessarOpen}
          levantamentoId={levId}
        />
      )}

      {/* Status badge */}
      <Card className="p-3 flex items-center gap-3">
        <span className="text-sm font-medium">Status:</span>
        <span
          data-testid="lev-status"
          className="text-sm text-muted-foreground"
        >
          {data.status}
        </span>
        {data.status === "criado" && (
          <span className="text-xs text-muted-foreground">
            · {data.videos.length} vídeo(s) anexado(s)
          </span>
        )}
        {/* #526 — aviso discreto de retenção do bruto; null (sem badge)
            quando bruto_expira_em ainda não se aplica. */}
        <RetentionBadge brutoExpiraEm={data.bruto_expira_em} />
      </Card>

      {/* #434 — banner de linhagem: superado (aponta pro sucessor) ou clone
          (aponta pra origem). Mutuamente exclusivos na prática (um clone só
          vira 'superado' quando o PRÓPRIO sucessor dele for validado). */}
      {data.vigencia === "superado" && (
        <Alert data-testid="banner-superado">
          <AlertTitle>Levantamento superado</AlertTitle>
          <AlertDescription>
            Este levantamento foi re-processado e substituído por uma versão
            mais recente já validada.{" "}
            {successorClone ? (
              <button
                type="button"
                className="underline"
                onClick={() => router.push(`/${successorClone.id}`)}
              >
                Ver versão vigente
              </button>
            ) : (
              "A versão vigente não foi encontrada na lista atual."
            )}
          </AlertDescription>
        </Alert>
      )}
      {data.origem_levantamento_id && (
        <p className="text-xs text-muted-foreground" data-testid="banner-clone-de">
          Esta é uma nova versão (re-processamento)
          {origemLevantamento ? (
            <>
              {" "}
              de{" "}
              <button
                type="button"
                className="underline"
                onClick={() =>
                  router.push(`/${data.origem_levantamento_id}`)
                }
              >
                {origemLevantamento.posto_contagem}
              </button>
            </>
          ) : null}
          .
        </p>
      )}

      {/* #426 — placar da fila (gesto 3): aguardando vaga/posição, etapa de
          processamento, ou erro + botão Reprocessar (gesto 2). */}
      {showFilaPlacar && (
        <FilaPlacar
          info={filaInfo}
          onReprocess={handleReprocess}
          isReprocessing={dispatchMut.isPending}
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Status-driven hub panel                                             */}
      {/* ------------------------------------------------------------------ */}

      {data.status === "criado" && (
        <InboxPanel levId={levId} associatedVideoIds={associatedVideoIds} />
      )}

      {data.status === "em_processamento" && (
        <ProcessingPanel
          avgProgress={avgProgress}
          videos={data.videos.map((v) => ({
            status: v.status,
            progress: String(v.progress),
            file_name: v.file_name,
          }))}
        />
      )}

      {(isProcessed || data.status === "validated") && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => router.push(`/${levId}/scene`)}
              data-testid="btn-edit-cut-lines"
            >
              Editar Cut Lines
            </Button>
            {/* #483 achado 1 — "Ver resultados" só aparece com um count_run
                real (Resultados sem count-run é beco sem saída). Antes disso
                o botão "Editar Cut Lines" acima é o único caminho — a etapa
                Calcular (dentro de scene) é quem produz o primeiro run. */}
            {hasRuns && (
              <Button
                variant="outline"
                data-testid="btn-ver-resultados"
                onClick={() => router.push(`/${levId}/results`)}
              >
                Ver resultados
              </Button>
            )}
            {/* #434 — só a partir de 'validated' (trabalho fechado); some se
                já existir um clone ativo (idx_levantamento_origem é UNIQUE
                no backend -- um 2º clique aqui sempre daria 409). */}
            {data.status === "validated" && !successorClone && (
              <Button
                variant="outline"
                data-testid="btn-reprocessar-validado"
                onClick={() => setReprocessarOpen(true)}
              >
                Reprocessar (nova versão)
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {data.videos.length} vídeo(s) processado(s).
          </p>
          {/* #483 achado 1 — guia a próxima etapa em vez de deixar o
              operador descobrir sozinho. */}
          {!hasRuns && (
            <p
              className="text-xs text-muted-foreground"
              data-testid="hint-proxima-etapa"
            >
              Próxima etapa: desenhe as cut lines e clique em{" "}
              <strong>Calcular</strong> para gerar a primeira contagem.
            </p>
          )}
        </div>
      )}

      {data.status === "erro" && (
        <Alert variant="destructive">
          <AlertTitle>Erro no processamento</AlertTitle>
          <AlertDescription>
            {data.videos
              .filter((v) => v.error_message)
              .map((v) => v.error_message)
              .join("; ") ||
              "Erro desconhecido. Verifique os logs do worker."}
          </AlertDescription>
        </Alert>
      )}
      </div>
    </div>
  );
}
